import { Component, Input, OnDestroy, OnInit, signal, WritableSignal, input, output, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { distinctUntilChanged, Observable, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { MatCheckboxChange, MatCheckbox } from '@angular/material/checkbox';
import { MatMenuTrigger, MatMenu } from '@angular/material/menu';
import { TranslatePipe } from '@ngx-translate/core';

import { TableFilterOption, TableFilterOptionValue } from '../table.component';
import { UniversalDropdownComponent } from '../../universal-dropdown/universal-dropdown.component';
import { LoadingComponent } from '../../loading/loading.component';
import { LoadingMode } from '../../../enums/LoadingMode.enum';
import { TABLE_MODE } from '../../../enums/TableMode.enum';

@Component({
  selector: 'app-filter-menu',
  templateUrl: './filter-menu.component.html',
  styleUrls: ['./filter-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatMenuTrigger,
    MatMenu,
    FormsModule,
    UniversalDropdownComponent,
    ReactiveFormsModule,
    MatCheckbox,
    TranslatePipe,
    LoadingComponent,
  ],
})
export class FilterMenuComponent implements OnInit, OnDestroy {
  readonly selector = input<string>();
  readonly filterType = input<'checkbox' | 'search'>('checkbox');
  readonly mode = input<TABLE_MODE>(TABLE_MODE.CLIENT);

  @Input() set filterOptions(options: TableFilterOption[] | Observable<TableFilterOption[]>) {
    if (options instanceof Observable) {
      this.filterOptionsObservable = options;
    } else {
      if (!this.arraysAreEqual(this._filterOptions, options)) {
        this._filterOptions = options;
      }
    }
  }

  protected isFormVisible = true;

  private _selectedFilters: TableFilterOptionValue[] = [];
  public selectedFiltersFormControl: FormControl<TableFilterOptionValue[]>;

  @Input() set selectedFilters(selectedFilters: TableFilterOptionValue[]) {
    this._selectedFilters = selectedFilters;
    if (this.selectedFiltersFormControl) {
      this.selectedFiltersFormControl.setValue(selectedFilters, { emitEvent: false });
    }
  }

  get selectedFilters(): TableFilterOptionValue[] {
    return this._selectedFilters;
  }

  readonly selectedFiltersChange = output<TableFilterOptionValue[]>();

  constructor() {
    this.selectedFiltersFormControl = new FormControl<TableFilterOptionValue[]>([]);
  }

  private toggleButtonClick$ = new Subject<void>();
  protected isFilterValuesLoading: WritableSignal<boolean> = signal(false);
  protected _filterOptions: TableFilterOption[] = [];
  protected readonly LoadingMode = LoadingMode;
  protected readonly TABLE_MODE = TABLE_MODE;
  protected filterOptionsObservable: Observable<TableFilterOption[]>;
  private destroy$: Subject<boolean> = new Subject<boolean>();

  ngOnInit(): void {
    if (this.filterType() === 'search') {
      this.subscribeToFilterChanges();
    }
    if (this.mode() === TABLE_MODE.SERVER) {
      this.subscribeToToggleButtonClick();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  protected displayFunctionFilters(filter: TableFilterOption): string {
    return filter ? (filter?.label ? filter.label.toString() : '(Empty)') : '(Empty)';
  }

  protected valueFunctionFilters(filter: TableFilterOption): TableFilterOptionValue {
    return filter ? filter.value : '';
  }

  public isSelected(filter: TableFilterOptionValue): boolean {
    return this.selectedFilters.includes(filter);
  }

  public toggleFilter(filter: TableFilterOptionValue): void {
    if (this.isSelected(filter)) {
      this.selectedFilters = this.selectedFilters.filter(f => f !== filter);
    } else {
      this.selectedFilters.push(filter);
    }
    this.emitClientSideSelectedFilterChange();
  }

  public changeFilters(filters: TableFilterOptionValue[]): void {
    this._selectedFilters = filters;
    this.emitClientSideSelectedFilterChange();
  }

  protected isSaveAllSelected(): boolean {
    return this.selectedFilters.length === this._filterOptions.length;
  }

  protected toggleSelectAll(event: MatCheckboxChange): void {
    this.selectedFilters = event.checked ? this._filterOptions.map(option => option.value) : [];
    this.emitClientSideSelectedFilterChange();
  }

  protected onToggleButtonClick(): void {
    this.toggleButtonClick$.next();
  }

  public applyFilters(): void {
    if (this.mode() === TABLE_MODE.SERVER) {
      this.isFormVisible = false;
      this.selectedFiltersChange.emit(this.selectedFilters);
    }
  }

  private subscribeToToggleButtonClick(): void {
    this.toggleButtonClick$
      .pipe(
        tap(() => this.isFilterValuesLoading.set(true)),
        switchMap(() => this.filterOptionsObservable),
        tap(columnFilterOptions => {
          this._filterOptions = columnFilterOptions;
          this.isFilterValuesLoading.set(false);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private subscribeToFilterChanges(): void {
    this.selectedFiltersFormControl.valueChanges
      .pipe(
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        tap(filters => {
          this.selectedFilters = filters;
          this.changeFilters(filters);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private arraysAreEqual(array1: TableFilterOption[], array2: TableFilterOption[]): boolean {
    return array1.length === array2.length && array1.every((value, index) => value.value === array2[index].value);
  }

  public get filterSearchOptions(): TableFilterOption[] | string[] {
    if (this.mode() === TABLE_MODE.SERVER) {
      return this._filterOptions;
    }
    return this._filterOptions.map(option => option.label);
  }

  private emitClientSideSelectedFilterChange(): void {
    if (this.mode() === TABLE_MODE.CLIENT) {
      this.selectedFiltersChange.emit(this.selectedFilters);
    }
  }
}
